import React from 'react';
import { REVIEWS } from './data.js';
// SEO helpers — dynamic per-page meta tags, Open Graph, Twitter cards, JSON-LD.
//
// useSEO({...}) updates the document head whenever the route changes:
//   - <title>
//   - <meta name="description">
//   - <meta name="keywords">
//   - <link rel="canonical">
//   - All og:* and twitter:* image/title/description tags
//   - JSON-LD structured-data block (per-page, in addition to the global LocalBusiness)
//
// Call useSEO at the top of each page component (HomePage / PlaagPage / StadPage).

const SITE_URL = "https://www.abcongedierte.nl";
const SITE_NAME = "ABC Ongediertebestrijding";
const DEFAULT_OG_IMAGE = SITE_URL + "/assets/og-cover.png";

function setMeta(name, content, useProperty = false) {
  if (!content) return;
  const attr = useProperty ? "property" : "name";
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function setJsonLd(id, data) {
  // Remove ALL previous blocks for this id (both single and array-suffixed).
  // The old code only removed `id` itself, leaving `id-0`, `id-1` etc. behind
  // — those accumulated on every navigation (memory leak + duplicate schema).
  document.querySelectorAll(`script[data-seo-id="${id}"]`).forEach(t => t.remove());
  const items = Array.isArray(data) ? data : [data];
  items.forEach((d, i) => {
    const t = document.createElement("script");
    t.type = "application/ld+json";
    t.id = items.length > 1 ? `${id}-${i}` : id;
    t.setAttribute("data-seo-id", id);
    t.textContent = JSON.stringify(d);
    document.head.appendChild(t);
  });
}

export function useSEO({ title, description, path = "/", image, jsonLd, ogType, noindex }) {
  React.useEffect(() => {
    // Title conventie: ALTIJD '<paginaspecifiek> | ABC Ongedierte' suffix.
    // Google's SERP-titel-limiet is ~60 chars; we kiezen consistent branding
    // boven 60-char-discipline (brand-recall + click-through). Page-specifiek
    // deel mag dus 43 chars worden (60 - 17 voor suffix).
    const SHORT_BRAND = " | ABC Ongedierte"; // 17 chars
    const fullTitle = title.includes("ABC Ongedierte")
      ? title // already has brand
      : title + SHORT_BRAND;
    // Canonical met trailing slash voor directory-paden, omdat Cloudflare Pages
    // 308-redirect doet van non-slash → slash. Canonical moet matchen wat de
    // browser daadwerkelijk laadt, anders ziet Google 2 URLs.
    const canonicalPath = path === "/" ? "/" : (path.endsWith("/") ? path : path + "/");
    const url = SITE_URL + canonicalPath;
    // Truncate description to 155 chars (Google SERP-snippet limit) for safety.
    // Knip altijd af op een zin-grens (`. `) als die binnen 155 chars zit,
    // anders op laatste woord-grens. Geen "..."/"…" toevoegen — Google
    // toont anders een extra ellipsis bovenop wat die zelf al doet.
    let safeDesc = description;
    if (description.length > 155) {
      const sub = description.substring(0, 155);
      const lastSent = sub.lastIndexOf('. ');
      if (lastSent > 80) {
        safeDesc = sub.substring(0, lastSent + 1);
      } else {
        safeDesc = sub.replace(/\s+\S*$/, '');
      }
    }
    const ogImage = image || DEFAULT_OG_IMAGE;

    document.title = fullTitle;
    setMeta("description", safeDesc);
    // meta keywords wordt door alle search engines genegeerd (Google sinds 2009).
    // Bewust niet meer settable — laat keyword-input weg uit alle useSEO calls.
    // Bestaande tag verwijderen indien aanwezig (vorige render had wel keywords).
    document.querySelector('meta[name="keywords"]')?.remove();

    // Robots: per-pagina noindex mogelijk (gebruikt door noindexed stad×plaag
    // combinaties). Default = index,follow zodat productie standaard rankt.
    setMeta("robots", noindex ? "noindex, follow" : "index, follow");

    setLink("canonical", url);

    setMeta("og:title", fullTitle, true);
    setMeta("og:description", safeDesc, true);
    setMeta("og:url", url, true);
    setMeta("og:image", ogImage, true);
    // og:type: 'article' alleen voor blogposts (caller geeft ogType door).
    // Alle andere routes (home, diensten, stad, plaag, contact, etc.) zijn
    // landingspagina's en horen og:type=website te krijgen.
    setMeta("og:type", ogType || "website", true);

    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", safeDesc);
    setMeta("twitter:image", ogImage);

    if (jsonLd) setJsonLd("page-jsonld", jsonLd);
    else {
      document.querySelectorAll('script[data-seo-id="page-jsonld"]').forEach(t => t.remove());
    }
  }, [title, description, path, image, ogType, noindex, JSON.stringify(jsonLd || null)]);
}

// Build a single Review JSON-LD object.
//
// Usage:
//   buildReviewSchema({
//     author: "M. de Vries",
//     rating: 5,
//     text: "Snel en professioneel geholpen!",
//     datePublished: "2026-05-12",
//     locality: "Rotterdam",
//   })
//
// When the Trustoo / Google API is wired in, this is the only function that
// changes — the caller just maps the live response into this shape.
//
// To embed reviews on the LocalBusiness entity, wrap a list with
// buildItemListReviews(reviews) and add the result to a page's `jsonLd`.
export function buildReviewSchema(r) {
  return {
    "@context": "https://schema.org",
    "@type": "Review",
    author: { "@type": "Person", name: r.author },
    reviewRating: {
      "@type": "Rating",
      ratingValue: String(r.rating ?? 5),
      bestRating: "5",
      worstRating: "1",
    },
    reviewBody: r.text,
    ...(r.datePublished ? { datePublished: r.datePublished } : {}),
    ...(r.locality ? {
      itemReviewed: {
        "@type": "LocalBusiness",
        "@id": SITE_URL + "/#organization",
        name: "ABC Ongediertebestrijding " + r.locality,
        areaServed: { "@type": "City", name: r.locality },
      }
    } : {
      itemReviewed: { "@id": SITE_URL + "/#organization" }
    }),
    ...(r.publisher ? { publisher: { "@type": "Organization", name: r.publisher } } : {}),
  };
}

// Helper: turn a list of plain review objects into an array of Review JSON-LD
// schemas (so a useSEO `jsonLd` array can spread them in).
export function buildItemListReviews(reviews) {
  if (!reviews || !reviews.length) return [];
  return reviews.map(buildReviewSchema);
}

// Build a BreadcrumbList JSON-LD given crumbs [{name, url}]
export function buildBreadcrumb(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: SITE_URL + c.url,
    })),
  };
}

// Build a Service / PestControlService schema for plaag pages.
// 2026-05-29: aggregateRating verwijderd (was company-wide rating gekopieerd
// naar elke plaag-pagina). Google's guidelines: aggregateRating moet matchen
// met page-content. 12 plaag-pagina's × dezelfde bedrijfsrating = rich-results
// spam risk. Sterren-snippets komen nu alleen op homepage + /reviews/.
export function buildPlaagSchema(plaag) {
  return {
    "@context": "https://schema.org",
    "@type": "PestControlService",
    "serviceType": `${plaag.nl} bestrijden`,
    "name": `${plaag.nl} bestrijden door ABC Ongediertebestrijding`,
    "description": plaag.intro,
    "provider": { "@id": SITE_URL + "/#organization" },
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Zuid-Holland" },
      { "@type": "AdministrativeArea", "name": "Noord-Holland" },
      { "@type": "AdministrativeArea", "name": "Utrecht" }
    ],
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "url": SITE_URL + "/" + plaag.slug + "bestrijding",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "EUR",
        "description": "Eerlijke prijs telefonisch vóór de afspraak, bel 085 500 50 50"
      }
    }
  };
}

// Build a LocalBusiness schema for stad pages
// 2026-05-29: aggregateRating verwijderd. 117 steden × dezelfde 4,6/159
// bedrijfsrating = textbook "review snippet spam" volgens Google's guidelines.
// Sterren-snippets blijven op homepage + /reviews/ pagina.
export function buildStadSchema(stad) {
  return {
    "@context": "https://schema.org",
    "@type": "PestControlService",
    "name": `ABC Ongediertebestrijding ${stad.naam}`,
    "description": `Ongediertebestrijding in ${stad.naam}. Spoeddienst 24/7, snelle inspectie ter plaatse, vakkundige aanpak met nacontrole.`,
    "url": SITE_URL + "/werkgebied/" + stad.slug,
    "telephone": "+31-85-500-50-50",
    "parentOrganization": { "@id": SITE_URL + "/#organization" },
    "areaServed": {
      "@type": "City",
      "name": stad.naam,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": stad.provincie
      }
    }
  };
}

